import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Add as AddIcon, CheckCircle, RadioButtonUnchecked, Close, 
         History, ExpandMore, Search, CalendarToday } from '@mui/icons-material';
import { getTrabajos, getExtras, createExtra } from '../services/api';
import { debounce } from 'lodash';

// Componente memoizado para campos de texto
const DebouncedTextField = React.memo(({ value, onChange, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  
  // Debounce the onChange callback
  const debouncedOnChange = useMemo(
    () => debounce(onChange, 300),
    [onChange]
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    debouncedOnChange(e.target.value);
  };

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel();
    };
  }, [debouncedOnChange]);

  return (
    <TextField
      value={localValue}
      onChange={handleChange}
      {...props}
    />
  );
});

// Componente ChecklistModal separado
const ChecklistModal = ({ 
  open, 
  onClose, 
  selectedPiso, 
  selectedDate, 
  checklistData, 
  setChecklistData, 
  checklistTasks, 
  handleTaskChange, 
  handleObservationsChange, 
  handleSaveChecklist, 
  calcularProgresoChecklist,
  isMobile 
}) => {
  const handleMarkAll = useCallback(() => {
    const allChecked = checklistTasks.every((_, i) => checklistData.tareas[i]);
    const newTareas = {};
    checklistTasks.forEach((_, i) => {
      newTareas[i] = !allChecked;
    });
    setChecklistData(prev => ({ ...prev, tareas: newTareas }));
  }, [checklistData.tareas, checklistTasks, setChecklistData]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          Checklist de {selectedPiso?.piso?.nombre} - {selectedDate.toLocaleDateString('es-ES')}
        </span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <DebouncedTextField
          label="Nombre de la limpiadora"
          value={checklistData.limpiadora}
          onChange={(value) => setChecklistData(prev => ({ ...prev, limpiadora: value }))}
          fullWidth
          margin="normal"
          size={isMobile ? "small" : "medium"}
        />
        
        <Box sx={{ my: 2 }}>
          <Typography variant="h6" gutterBottom>
            Progreso: {Math.round(calcularProgresoChecklist())}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={calcularProgresoChecklist()} 
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
        
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Tareas
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button 
            variant="outlined" 
            size={isMobile ? "small" : "medium"}
            onClick={handleMarkAll}
          >
            {checklistTasks.every((_, i) => checklistData.tareas[i]) 
              ? "Desmarcar todo" 
              : "Marcar todo"}
          </Button>
        </Box>

        <FormGroup 
          sx={{ 
            maxHeight: 300, 
            overflow: 'auto', 
            mb: 2,
          }}
        >
          {checklistTasks.map((task, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  checked={!!checklistData.tareas[index]}
                  onChange={() => handleTaskChange(index)}
                  icon={<RadioButtonUnchecked />}
                  checkedIcon={<CheckCircle color="primary" />}
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={<Typography variant={isMobile ? "body2" : "body1"}>{task}</Typography>}
              sx={{ alignItems: 'flex-start', my: 0.5 }}
            />
          ))}
        </FormGroup>
        
        <Typography variant="h6" gutterBottom>
          Observaciones
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
          <DebouncedTextField
            label="Productos que he dejado en el piso"
            multiline
            rows={2}
            value={checklistData.observaciones.productosDejados}
            onChange={(value) => handleObservationsChange('productosDejados', value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
          <DebouncedTextField
            label="Productos que hace falta llevar (faltan)"
            multiline
            rows={2}
            value={checklistData.observaciones.productosFaltan}
            onChange={(value) => handleObservationsChange('productosFaltan', value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
          <DebouncedTextField
            label="Ropa de propietarios que he dejado en el almacén"
            multiline
            rows={2}
            value={checklistData.observaciones.ropaAlmacen}
            onChange={(value) => handleObservationsChange('ropaAlmacen', value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
          <DebouncedTextField
            label="Limpieza cristales (indicar cuáles)"
            multiline
            rows={2}
            value={checklistData.observaciones.limpiezaCristales}
            onChange={(value) => handleObservationsChange('limpiezaCristales', value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
          <DebouncedTextField
            label="Cambio de fundas o colchas (indicar cuáles)"
            multiline
            rows={2}
            value={checklistData.observaciones.cambioFundas}
            onChange={(value) => handleObservationsChange('cambioFundas', value)}
            fullWidth
            size={isMobile ? "small" : "medium"}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          fullWidth={isMobile}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleSaveChecklist} 
          variant="contained" 
          fullWidth={isMobile}
        >
          Guardar Checklist
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Componente para el historial de checklists
const ChecklistHistory = ({ open, onClose, isMobile }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredChecklists, setFilteredChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);

  // Obtener todas las checklists del localStorage
  const getAllChecklists = useCallback(() => {
    const checklists = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('checklist-')) {
        try {
          const checklist = JSON.parse(localStorage.getItem(key));
          // Extraer información de la clave
          const keyParts = key.split('-');
          const pisoId = keyParts[1];
          const fecha = keyParts[2];
          
          checklists.push({
            ...checklist,
            key,
            pisoId,
            fecha
          });
        } catch (e) {
          console.error('Error parsing checklist:', e);
        }
      }
    }
    return checklists.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, []);

  // Filtrar checklists basado en el término de búsqueda
  useEffect(() => {
    const allChecklists = getAllChecklists();
    if (!searchTerm) {
      setFilteredChecklists(allChecklists);
    } else {
      const filtered = allChecklists.filter(checklist => 
        checklist.piso?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checklist.limpiadora?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checklist.fecha?.includes(searchTerm)
      );
      setFilteredChecklists(filtered);
    }
  }, [searchTerm, getAllChecklists]);

  const handleViewChecklist = (checklist) => {
    setSelectedChecklist(checklist);
  };

  const handleCloseDetail = () => {
    setSelectedChecklist(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Historial de Checklists</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {selectedChecklist ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Checklist de {selectedChecklist.piso} - {new Date(selectedChecklist.fecha).toLocaleDateString('es-ES')}
              </Typography>
              <Button onClick={handleCloseDetail} variant="outlined" size="small">
                Volver al listado
              </Button>
            </Box>
            
            <Typography variant="body1" gutterBottom>
              <strong>Limpiadora:</strong> {selectedChecklist.limpiadora}
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Productos que faltaban:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, p: 1, backgroundColor: '#fff3cd', borderRadius: 1 }}>
              {selectedChecklist.observaciones?.productosFaltan || 'No se registraron productos faltantes'}
            </Typography>
            
            <Typography variant="h6" gutterBottom>
              Otras observaciones:
            </Typography>
            
            {selectedChecklist.observaciones?.productosDejados && (
              <Typography variant="body2" gutterBottom>
                <strong>Productos dejados:</strong> {selectedChecklist.observaciones.productosDejados}
              </Typography>
            )}
            
            {selectedChecklist.observaciones?.ropaAlmacen && (
              <Typography variant="body2" gutterBottom>
                <strong>Ropa en almacén:</strong> {selectedChecklist.observaciones.ropaAlmacen}
              </Typography>
            )}
            
            {selectedChecklist.observaciones?.limpiezaCristales && (
              <Typography variant="body2" gutterBottom>
                <strong>Limpieza de cristales:</strong> {selectedChecklist.observaciones.limpiezaCristales}
              </Typography>
            )}
            
            {selectedChecklist.observaciones?.cambioFundas && (
              <Typography variant="body2" gutterBottom>
                <strong>Cambio de fundas/colchas:</strong> {selectedChecklist.observaciones.cambioFundas}
              </Typography>
            )}
            
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Tareas completadas: {Object.values(selectedChecklist.tareas || {}).filter(Boolean).length} / 26
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Search sx={{ mr: 1 }} />
              <TextField
                placeholder="Buscar por piso, limpiadora o fecha..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
              />
            </Box>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {filteredChecklists.length} checklists encontradas
            </Typography>
            
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredChecklists.length === 0 ? (
                <Typography variant="body1" align="center" sx={{ py: 4 }}>
                  No se encontraron checklists
                </Typography>
              ) : (
                filteredChecklists.map((checklist, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="subtitle1">
                          {checklist.piso}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {new Date(checklist.fecha).toLocaleDateString('es-ES')} - {checklist.limpiadora}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" gutterBottom>
                        <strong>Productos que faltaban:</strong> {checklist.observaciones?.productosFaltan || 'Ninguno registrado'}
                      </Typography>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={() => handleViewChecklist(checklist)}
                        sx={{ mt: 1 }}
                      >
                        Ver detalles completos
                      </Button>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

const DailyView = () => {
  const [selectedDate] = useState(new Date());
  const [trabajos, setTrabajos] = useState([]);
  const [extras, setExtras] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openChecklistDialog, setOpenChecklistDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedPiso, setSelectedPiso] = useState(null);
  const [nuevoExtra, setNuevoExtra] = useState({ minutos: '', comentario: '' });
  const [checklistData, setChecklistData] = useState({
    limpiadora: '',
    tareas: {},
    observaciones: {
      productosDejados: '',
      productosFaltan: '',
      ropaAlmacen: '',
      limpiezaCristales: '',
      cambioFundas: ''
    }
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Tareas del checklist (las mismas que en tu imagen)
  const checklistTasks = useMemo(() => [
    "Tirar comida en caso de que hayan dejado cosas abiertas o perecederas.",
    "Revisar y limpiar si hace falta la nevera, horno, microondas, cafetera.",
    "Recoger los cacharros sucios y los del lavavajillas (si hubiera).",
    "Limpiar vitrocerámica y encimera.",
    "Limpiar campana extractora.",
    "Limpiar las sillas del comedor.",
    "Revisar balcón y terraza si hubiera. Tiene que quedar limpio y recogido.",
    "Pasar aspiradora por cocina, salón, habitaciones, baño, recibidor y comedor.",
    "Dejar bayeta de cocina nueva y estropajo nuevo con cada limpieza.",
    "Chequear los armarios de toda la casa (limpios y con perchas colgadas)",
    "Hacer camas y colocar los juegos de toallas bien doblados sobre ellas.",
    "Pasar la aspiradora por toda la casa, también debajo de las camas.",
    "Colocar (si estuvieran mal) los folletos de publicidad.",
    "Limpiar polvo de mesitas, lámparas.",
    "Pasar fregona por baño y cocina (resto de la casa si hiciera falta)",
    "Poner bandas en las tapas de los inodoros, intentar que queden rectas y sin arrugar.",
    "Revisar que queda champú, gel, fairy, papel higiénico. Si falta ANOTARLO.",
    "Si hay rollos de papel higiénico que tenga más de la mitad, poner pegatina. Dejarlo repuesto",
    "Poner una alfombrilla nueva en cada baño.",
    "Limpiar bien las mamparas y los espejos del baño.",
    "Limpiar bien inodoros y pasar grifos para QUE BRILLEN.",
    "Poner un rollo de papel nuevo en cada baño y dejar uno de repuesto en cada baño.",
    "Revisar los cristales y anotar cuando fue la última vez. Sólo con 1 o 2 camas.",
    "Tirar la basura (en caso de que haya) y poner una bolsa nueva.",
    "Dejar en la cocina un trapo de cocina limpio.",
    "Revisar que está apagada la calefacción. Si no sabéis apagarla LLAMAR Y PREGUNTAR."
  ], []);

const loadDatosDia = useCallback(async () => {
  const fechaStr = selectedDate.toISOString().split('T')[0];

  try {
    const [trabajosResponse, extrasResponse] = await Promise.all([
      getTrabajos(),
      getExtras()
    ]);

    const trabajosDelDia = trabajosResponse.data.filter(trabajo => trabajo.fecha === fechaStr);
    const extrasDelDia = extrasResponse.data.filter(extra => extra.fecha === fechaStr);

    setTrabajos(trabajosDelDia);
    setExtras(extrasDelDia);

    // Guardamos en localStorage como respaldo offline
    localStorage.setItem(`trabajos-${fechaStr}`, JSON.stringify(trabajosDelDia));
    localStorage.setItem(`extras-${fechaStr}`, JSON.stringify(extrasDelDia));

  } catch (error) {
    console.warn('No hay conexión. Cargando datos offline desde localStorage.', error);

    // Recuperar datos offline
    const trabajosOffline = JSON.parse(localStorage.getItem(`trabajos-${fechaStr}`) || '[]');
    const extrasOffline = JSON.parse(localStorage.getItem(`extras-${fechaStr}`) || '[]');

    setTrabajos(trabajosOffline);
    setExtras(extrasOffline);
  }
}, [selectedDate]);


  useEffect(() => {
    loadDatosDia();
  }, [loadDatosDia]);

  const calcularTotalHoras = useCallback(() => {
    const totalMinutos = trabajos.reduce((sum, trabajo) => sum + trabajo.duracion, 0) +
                         extras.reduce((sum, extra) => sum + extra.minutos, 0);
    return (totalMinutos / 60).toFixed(2);
  }, [trabajos, extras]);

  const calcularHoraSalida = useCallback(() => {
    const totalMinutos = trabajos.reduce((sum, trabajo) => sum + trabajo.duracion, 0) +
                         extras.reduce((sum, extra) => sum + extra.minutos, 0);
    const horaSalida = new Date(selectedDate);
    horaSalida.setHours(12, totalMinutos, 0);
    return horaSalida.toTimeString().split(' ')[0].substring(0, 5);
  }, [trabajos, extras, selectedDate]);

  const handleAddExtra = useCallback(async () => {
    try {
      const minutos = parseInt(nuevoExtra.minutos);
      if (isNaN(minutos) || minutos <= 0) {
        alert('Por favor, introduce minutos válidos');
        return;
      }

      await createExtra({
        fecha: selectedDate.toISOString().split('T')[0],
        minutos: minutos,
        comentario: nuevoExtra.comentario || null
      });
      
      setOpenDialog(false);
      setNuevoExtra({ minutos: '', comentario: '' });
      loadDatosDia();
    } catch (error) {
      console.error('Error adding extra:', error);
      alert('Error al añadir el extra: ' + (error.response?.data?.detail || error.message));
    }
  }, [nuevoExtra, selectedDate, loadDatosDia]);
  
  const handleOpenChecklist = useCallback((piso) => {
    setSelectedPiso(piso);
    // Cargar datos guardados si existen
    const savedChecklist = localStorage.getItem(`checklist-${piso.id}-${selectedDate.toISOString().split('T')[0]}`);
    if (savedChecklist) {
      setChecklistData(JSON.parse(savedChecklist));
    } else {
      setChecklistData({
        limpiadora: '',
        tareas: {},
        observaciones: {
          productosDejados: '',
          productosFaltan: '',
          ropaAlmacen: '',
          limpiezaCristales: '',
          cambioFundas: ''
        }
      });
    }
    setOpenChecklistDialog(true);
  }, [selectedDate]);

  const handleSaveChecklist = useCallback(() => {
    const checklistToSave = {
      ...checklistData,
      piso: selectedPiso.piso?.nombre,
      fecha: selectedDate.toISOString().split('T')[0]
    };
    localStorage.setItem(`checklist-${selectedPiso.id}-${selectedDate.toISOString().split('T')[0]}`, JSON.stringify(checklistToSave));
    setOpenChecklistDialog(false);
    alert('Checklist guardado correctamente');
  }, [checklistData, selectedPiso, selectedDate]);

  const handleTaskChange = useCallback((index) => {
    setChecklistData(prev => ({
      ...prev,
      tareas: {
        ...prev.tareas,
        [index]: !prev.tareas[index]
      }
    }));
  }, []);

  const handleObservationsChange = useCallback((field, value) => {
    setChecklistData(prev => ({
      ...prev,
      observaciones: {
        ...prev.observaciones,
        [field]: value
      }
    }));
  }, []);

  const calcularProgresoChecklist = useCallback(() => {
    const tareasCompletadas = Object.values(checklistData.tareas).filter(Boolean).length;
    return (tareasCompletadas / checklistTasks.length) * 100;
  }, [checklistData.tareas, checklistTasks.length]);

  return (
    <Paper elevation={3} sx={{ p: isMobile ? 1 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }}>
          {selectedDate.toLocaleDateString('es-ES', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<History />}
          onClick={() => setOpenHistoryDialog(true)}
          size={isMobile ? "small" : "medium"}
        >
          Ver Historial
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Pisos Programados
        </Typography>
        <List>
          {trabajos.map((trabajo) => (
            <ListItem 
              key={trabajo.id}
              sx={{
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                borderBottom: '1px solid #eee',
                py: 2
              }}
            >
              <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                mb: isMobile ? 1 : 0
              }}>
                <ListItemText
                  primary={trabajo.piso?.nombre || 'Piso sin nombre'}
                  secondary={`${trabajo.duracion} minutos ${trabajo.entrada_salida ? '⚡ (Entrada/Salida)' : ''}`}
                  sx={{ mb: isMobile ? 1 : 0 }}
                />
                
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: isMobile ? 'row-reverse' : 'row',
                  justifyContent: isMobile ? 'space-between' : 'flex-end',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  {trabajo.entrada_salida && (
                    <Chip 
                      label="Entrada/Salida" 
                      color="error" 
                      size="small" 
                      sx={{ 
                        mr: isMobile ? 0 : 1, 
                        ml: isMobile ? 1 : 0,
                        mb: isMobile ? 1 : 0
                      }} 
                    />
                  )}
                  
                  <Button 
                    variant="outlined"
                    onClick={() => handleOpenChecklist(trabajo)}
                    size={isMobile ? "small" : "medium"}
                    sx={{ minWidth: isMobile ? '100px' : 'auto' }}
                  >
                    Checklist
                  </Button>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Extras
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ mb: 2 }}
          size={isMobile ? "small" : "medium"}
        >
          Añadir Extra
        </Button>
        <List>
          {extras.map((extra) => (
            <ListItem key={extra.id}>
              <ListItemText
                primary={`${extra.minutos} minutos`}
                secondary={extra.comentario || 'Sin comentario'}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="h6">
          Resumen del Día
        </Typography>
        <Typography>Hora de entrada: 12:00</Typography>
        <Typography>Hora de salida: {calcularHoraSalida()}</Typography>
        <Typography>Total horas: {calcularTotalHoras()}h</Typography>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen={isMobile}>
        <DialogTitle>
          Añadir Horas Extra
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Minutos"
            type="number"
            fullWidth
            variant="outlined"
            value={nuevoExtra.minutos}
            onChange={(e) => setNuevoExtra(prev => ({...prev, minutos: e.target.value}))}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
          />
          <TextField
            margin="dense"
            label="Comentario (opcional)"
            fullWidth
            variant="outlined"
            value={nuevoExtra.comentario}
            onChange={(e) => setNuevoExtra(prev => ({...prev, comentario: e.target.value}))}
            size={isMobile ? "small" : "medium"}
          />
        </DialogContent>
        <DialogActions sx={{ flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            variant="outlined"
            fullWidth={isMobile}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAddExtra}
            variant="contained"
            disabled={!nuevoExtra.minutos || parseInt(nuevoExtra.minutos) <= 0}
            fullWidth={isMobile}
          >
            Añadir
          </Button>
        </DialogActions>
      </Dialog>

      <ChecklistModal
        open={openChecklistDialog}
        onClose={() => setOpenChecklistDialog(false)}
        selectedPiso={selectedPiso}
        selectedDate={selectedDate}
        checklistData={checklistData}
        setChecklistData={setChecklistData}
        checklistTasks={checklistTasks}
        handleTaskChange={handleTaskChange}
        handleObservationsChange={handleObservationsChange}
        handleSaveChecklist={handleSaveChecklist}
        calcularProgresoChecklist={calcularProgresoChecklist}
        isMobile={isMobile}
      />

      <ChecklistHistory
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        isMobile={isMobile}
      />
    </Paper>
  );
};

export default DailyView;